//
// # Entity Map Factory
//
// Constructs `CRDTMap` instances for design state. Phase 4 made the
// CRDT path unconditional — the factory no longer chooses between
// `IdMap` and `CRDTMap`. `IdMap` is still exported from this package
// for non-design uses (e.g. `SchematicManager.Instances/Pins/Wires`),
// but design entity maps always go through here.
//

import { IEntityMap, HasId } from "./entity_map";
import { CRDTMap } from "./crdt_map";

// Configuration for creating an entity map. clientId is required —
// every CRDT actor needs an identity for write-tagging.
export interface EntityMapConfig {
  // Client ID stamped into Lamport timestamps for writes through this
  // map. Per-Client today; if/when state becomes truly shared (one
  // map shared by N clients) the per-write timestamp's clientId comes
  // from the writing client, not from here.
  clientId: string;

  // Initial Lamport clock value. Defaults to 0.
  initialClock?: number;
}

export function createEntityMap<T extends HasId>(
  config: EntityMapConfig
): CRDTMap<T> {
  return new CRDTMap<T>(config.clientId, config.initialClock ?? 0);
}

// Type-narrowing guard. Always true today (every design entity map is
// a CRDTMap post-D5), but kept for type narrowing so callers can use
// CRDTMap-specific methods on `IEntityMap`-typed fields without a
// type assertion. Will collapse when the design state types switch
// from `IEntityMap` to `CRDTMap` directly.
export function isCRDTMap<T extends HasId>(
  map: IEntityMap<T>
): map is CRDTMap<T> {
  return map instanceof CRDTMap;
}
